import { Location, CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { GoogleMap } from '@capacitor/google-maps';
import { environment } from '../../../environments/environment';
import { Router, ActivatedRoute } from '@angular/router';
import { IonicModule, LoadingController } from '@ionic/angular';
import { GeolocationService } from 'src/app/services/geolocation.service';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { AppStorageService } from '../../services/app-storage.service';
import { StorageKeys } from '../../services/storage-keys';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { GeocodingService } from 'src/app/services/geocoding.service';

// Interfaz para opciones de GoogleMap
declare global {
  interface Window {
    googleMapsLoaded: boolean;
    googleMapsCallbacks: (() => void)[];
    onGoogleMapsReady: (callback: () => void) => void;
    initGoogleMaps: () => void;
  }
}

interface GoogleMapOptions {
  apiKey: string;
  id: string;
  element: HTMLElement;
  forceCreate?: boolean;
  config: {
    center: {
      lat: number;
      lng: number;
    };
    zoom: number;
    [key: string]: any;
  };
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MapComponent implements OnInit, AfterViewInit {
  @Output() close = new EventEmitter();
  @Output() locationSelected = new EventEmitter<{
    lat: number;
    lng: number;
    address: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  }>();

  @Input() isWeb: boolean = false;
  @Input() userLocation?: { lat: number; lng: number } | null = null;
  @Input() showRangeSelector: boolean = false;
  @Input() initialRange: number = 0;

  @ViewChild('map') mapRef!: ElementRef;

  range: number = 0;
  map: any; // Puede ser GoogleMap para Capacitor o google.maps.Map para web
  markers: any[] = []; // Almacena los marcadores creados
  userMarker: any; // Marcador para la ubicación del usuario
  selectedLocationMarker: any; // Marcador para la ubicación seleccionada

  // Centro predeterminado del mapa (Madrid, España)
  defaultCenter = { lat: 40.4168, lng: -3.7038 };
  selectedLocation: { lat: number; lng: number } | null = null;
  selectedAddress: string = '';

  // Detalles adicionales de la dirección
  addressDetails: {
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  } = {};

  // API Key de Google Maps desde environment
  apiKey: string = environment.googleMaps?.apiKey || ''; // Se debe definir en environment.ts

  // Geocoder para obtener direcciones a partir de coordenadas (para Google Maps)
  geocoder: any;

  // Propiedades para el estado de carga
  isLoadingAddress: boolean = false;
  isProcessing: boolean = false;

  // Agregar propiedad para el modo
  currentMode: string = '';

  constructor(
    private location: Location,
    private changeDetector: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private geolocationService: GeolocationService,
    private loadingController: LoadingController,
    public translateConfigService: TranslateConfigService,
    private storage: AppStorageService,
    private translate: TranslateService,
    private geocodingService: GeocodingService
  ) {}

  private loading?: HTMLIonLoadingElement | null = null;

  private async presentLoading(message = this.translate.instant('MAP.GETTING_LOCATION')) {
    try {
      if (this.loading) return;
      this.loading = await this.loadingController.create({
        message,
        spinner: 'crescent',
      });
      await this.loading.present();
    } catch (e) {
    }
  }

  private async dismissLoading() {
    try {
      if (this.loading) {
        await this.loading.dismiss();
        this.loading = null;
      }
    } catch (e) {
      // ignore
      this.loading = null;
    }
  }

  // Intentar obtener la ubicación del usuario siempre al inicio
  ngOnInit() {
   // Activar clase para deshabilitar fondo azul nativo
   try { document.body.classList.add('map-screen-active'); } catch {}
    // Determinar si estamos en web como fallback
    const platform = Capacitor.getPlatform && typeof Capacitor.getPlatform === 'function' ? Capacitor.getPlatform() : undefined;
    this.isWeb = !(platform === 'ios' || platform === 'android');

    // NOTE: Capacitor's WKWebView serves app files from http://localhost (or capacitor://localhost)
    // on iOS and Android. Do NOT treat hostname 'localhost' alone as a sign of livereload.
    // Only force web mode when the detected platform is actually 'web' (i.e. running in a browser).
    try {
      const hostname = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
      if ((hostname === 'localhost' || hostname === '127.0.0.1') && (platform === undefined || platform === 'web')) {
        this.isWeb = true;
      }
    } catch (e) {}

    // Obtener parámetros de la URL
    this.route.queryParams.subscribe((params) => {
      if (params['mode']) {
        this.currentMode = params['mode'];
      }
    });

    // Obtener datos del estado de navegación si existen
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      const currentLocation = navigation.extras.state['currentLocation'];
      const currentAddress = navigation.extras.state['currentAddress'];
      const currentRange = navigation.extras.state['currentRange'];
      const showRangeSelector = navigation.extras.state['showRangeSelector'];

      // Para modo filter o edit-post, usar ubicación pasada como inicial
      if (
        (this.currentMode === 'edit-post' || this.currentMode === 'filter') &&
        currentLocation &&
        currentLocation.lat &&
        currentLocation.lng
      ) {
        this.selectedLocation = currentLocation;
        this.selectedAddress = currentAddress || '';

        // Establecer centro del mapa
        this.defaultCenter = {
          lat: currentLocation.lat,
          lng: currentLocation.lng,
        };
      }

      // Para modo filter, configurar el selector de rango
      if (this.currentMode === 'filter') {
        this.showRangeSelector = showRangeSelector || true;
        this.range = currentRange || 0;
      }
    }

    // Early geolocation attempt removed: location acquisition now
    // happens during map initialization so the loader stays visible
    // until the map is fully ready.

    // Inicializar el rango con el valor recibido
    if (this.initialRange !== undefined) {
      this.range = this.initialRange;
    }
  }

  async ngAfterViewInit() {
    // Esperar más tiempo en iOS para asegurar que el DOM esté listo
    const isIOS = Capacitor.getPlatform() === 'ios';
    const waitTime = isIOS ? 800 : 300;

    setTimeout(async () => {
      await this.initializeMap();

      // Solo para edit-post: agregar marcador inicial si ya tenemos ubicación
      if (this.currentMode === 'edit-post' && this.selectedLocation) {
        await this.updateSelectedLocationMarker();

        // Si tenemos una dirección pero está vacía, obtenerla
        if (!this.selectedAddress || this.selectedAddress === '') {
          await this.getAddressFromCoordinates(this.selectedLocation);
        }
      }
    }, waitTime);
  }

  // REMOVER EL MÉTODO initMap() DUPLICADO - Solo usar initializeMap()
  // Mantener solo este método para inicializar el mapa
  async initializeMap() {
    // Show a single loader that remains until the map is fully created
    await this.presentLoading(this.translateConfigService.getItemTranslated('FORM_INPUTS.MAP.LOADING_ICON'));
    try {

      // Esperar a que Google Maps se cargue completamente
      try {
        await this.waitForGoogleMaps();
      } catch (error) {
        alert(
          this.translate.instant('MAP.GOOGLE_MAPS_LOAD_FAILED')
        );
        return;
      }
      if (this.isWeb) {
        await this.initWebMap();
      } else {
        await this.initMobileMap();
      }

    } catch (error) {
      // Mostrar mensaje de error al usuario
      alert(this.translate.instant('MAP.MAP_LOAD_ERROR'));
    } finally {
      await this.dismissLoading();
    }
  }

  // Método para esperar a que Google Maps se cargue completamente
  private waitForGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {

      // Si Google Maps ya está disponible, resolver inmediatamente
      if (typeof google !== 'undefined' && google.maps) {
        resolve();
        return;
      }

      // Usar el sistema de callback global si está disponible
      if (typeof window !== 'undefined' && window.onGoogleMapsReady) {
        const timeout = setTimeout(() => {
          reject(new Error(this.translate.instant('MAP.TIMEOUT_CALLBACK')));
        }, 15000); // Aumentar timeout a 15 segundos

        window.onGoogleMapsReady(() => {
          clearTimeout(timeout);
          resolve();
        });
        return;
      }

      // Fallback al método anterior
      const timeout = setTimeout(() => {
        reject(new Error(this.translate.instant('MAP.TIMEOUT')));
      }, 15000);

      const checkGoogleMaps = () => {
        if (typeof google !== 'undefined' && google.maps) {
          clearTimeout(timeout);
          resolve();
        } else {
          // Verificar cada 200ms (menos frecuente)
          setTimeout(checkGoogleMaps, 200);
        }
      };

      checkGoogleMaps();
    });
  }

  // Modificar initWebMap para recibir el centro como parámetro
  async initWebMap() {


    if (typeof google === 'undefined' || !google.maps) {
      alert(this.translate.instant('MAP.GOOGLE_MAPS_UNAVAILABLE'));
      return;
    }
    // Ensure we have a user location (may timeout). Loader is managed
    // by initializeMap so we don't present/dismiss here to avoid flashing.
    await this.getUserLocation();

    const mapCenter = this.userLocation || this.defaultCenter;

    const mapOptions = {
      center: mapCenter,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      styles: [],
    };

    this.map = new google.maps.Map(this.mapRef.nativeElement, mapOptions);
    this.geocoder = new google.maps.Geocoder();

    // Intentar recentrar inmediatamente si ya tenemos userLocation
    if (this.userLocation) {
      try {
        this.map.setCenter(this.userLocation);
        this.map.setZoom(15);
      } catch {}
    }

    // Configurar el evento de clic en el mapa
    this.map.addListener('click', async (event: any) => {
      const clickedLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      this.selectedLocation = clickedLocation;
      await this.updateSelectedLocationMarker();
      await this.getAddressFromCoordinates(clickedLocation);
    });

    // Removed redundant getUserLocation(); web centering handled above
  }

  // Modificar initMobileMap para recibir el centro como parámetro
  async initMobileMap(center?: { lat: number; lng: number }) {
    await setTimeout(() => {}, 100); // Small delay to ensure DOM is ready

    try {
      // Obtain user location once (may timeout). Loader is managed by
      // initializeMap so don't present/dismiss here to avoid early hide.

      const mapCenterCandidate = center || this.userLocation || this.defaultCenter;

      // Verificar que el elemento del mapa esté disponible
      if (!this.mapRef || !this.mapRef.nativeElement) {
        return;
      }

      // Verificar que el elemento tenga dimensiones
      const element = this.mapRef.nativeElement;
      if (!element.style.height) {
        element.style.height = Math.max(window.innerHeight * 0.55, 320) + 'px';
      }
      if (!element.offsetWidth || !element.offsetHeight) {
        // fallback explícito viewport
        element.style.width = window.innerWidth + 'px';
        element.style.height = Math.max(window.innerHeight * 0.55, 320) + 'px';
        await new Promise((r) => setTimeout(r, 250));
      }


      // Medidas exactas del elemento (algunos dispositivos requieren valores explícitos)
      let bounds = element.getBoundingClientRect();
      if (!bounds.width || !bounds.height) {
        await new Promise((r) => setTimeout(r, 200));
        bounds = element.getBoundingClientRect();
      }
      // Attempt to get user location now that bounds are available. This
      // may timeout but will populate this.userLocation if successful.
      await this.getUserLocation();

      const mapCenter = center || this.userLocation || this.defaultCenter;

      const mapOptions: GoogleMapOptions = {
        apiKey: this.apiKey,
        id: 'map',
        element: element,
        forceCreate: true,
        config: {
          center: mapCenter,
          zoom: 15,
          width: bounds.width,
          height: bounds.height,
          x: bounds.x,
          y: bounds.y,
          androidLiteMode: false,
          devicePixelRatio: window.devicePixelRatio,
          styles: [],
        },
      };


      try {
        this.map = await GoogleMap.create(mapOptions);
      } catch (createError) {
        // Intentar continuar sin bloquear la UI: mostrar mensaje y salir
        // No rethrow para evitar crash; el componente seguirá funcional sin mapa nativo
        return;
      }

      // Limpiar posibles fondos ascendentes que tapen el mapa
      try {
        let parent: HTMLElement | null = element.parentElement;
        let depth = 0;
        while (parent && depth < 5) {
          const bg = window.getComputedStyle(parent).backgroundColor;
          if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
            parent.style.background = 'transparent';
          }
          parent = parent.parentElement;
          depth++;
        }
        element.style.background = 'transparent';
        (element as any).classList.add('native-map-active');
      } catch {}

      // Asegurar que no hay overlay accidental encima
      try {
        const overlays = document.querySelectorAll('ion-content, .map-container');
        overlays.forEach((ov: any) => {
          if (ov !== element) {
            ov.style.willChange = 'auto';
          }
        });
      } catch {}


      // Después de crear el mapa, intentar centrar en la ubicación del usuario si la tenemos
      try {
        await this.centerMapOnUser();
      } catch (centerErr) {
      }

      // Small pause to allow native map surface to render and avoid a
      // short black flash on some Android devices
      try {
        await new Promise((r) => setTimeout(r, 300));
      } catch {}

      // Configurar el evento de clic en el mapa
      await this.map.setOnMapClickListener((event: any) => {
        const clickedLocation = {
          lat: event.latitude,
          lng: event.longitude,
        };
        this.selectedLocation = clickedLocation;
        this.updateSelectedLocationMarker();
        this.getAddressFromCoordinates(clickedLocation);
      });
    } catch (error) {
    }
  }

  // Manual resize trigger (can be invoked externally if needed)
  forceResize() {
    try {
      (this.map as any)?.resize?.();
    } catch {}
  }

  // Simplificar getUserLocation - solo obtener ubicación sin inicializar mapa
  getUserLocation() {
    // Prefer using GeolocationService which handles native vs web and caching
    // Add a timeout to avoid long hangs
    const timeoutMs = 7000;
    return new Promise<void>((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          // Do not change loader state here; resolve so map init continues
          // with existing this.userLocation (if any) or default center.
          resolve();
        }
      }, timeoutMs);

      (this.geolocationService.getPositionWithPermissionHandling() as any).subscribe({
        next: (pos: any) => {
          if (pos && pos.coords) {
            this.userLocation = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            };
            // store cache on window for backward compatibility
            try {
              const w: any = window as any;
              w.__lastGeoFix = { ts: Date.now(), coords: { ...this.userLocation } };
            } catch {}
            this.centerMapOnUser().finally(() => {
              if (!settled) {
                settled = true;
                clearTimeout(timer);
                resolve();
              }
            });
            return;
          }
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve();
          }
        },
        error: (err: any) => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve();
          }
        },
        complete: () => {},
      });
    });
  }

  // Nuevo método para centrar el mapa en la ubicación del usuario
  async centerMapOnUser() {
    if (!this.userLocation || !this.map) return;

    try {
      if (this.isWeb) {
        this.map.setCenter(this.userLocation);
      } else {
        await this.map.setCamera({
          coordinate: this.userLocation,
          zoom: 15,
          animate: true,
        });
      }
    } catch (error) {
    }
  }

  // REMOVER COMPLETAMENTE addUserLocationMarker() para create-post
  // Solo se usará updateSelectedLocationMarker()

  // Actualizar el marcador de ubicación seleccionada - VERSIÓN SIMPLIFICADA
  async updateSelectedLocationMarker() {
    if (!this.selectedLocation || !this.map) {
      return;
    }

    try {
      if (this.isWeb) {
        // Eliminar marcador anterior si existe
        if (this.selectedLocationMarker) {
          this.selectedLocationMarker.setMap(null);
        }

        // Determinar si el marcador debe ser arrastrable
        const isDraggable = true;

        // Crear nuevo marcador
        this.selectedLocationMarker = new google.maps.Marker({
          position: this.selectedLocation,
          map: this.map,
          draggable: isDraggable,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: this.currentMode === 'edit-post' ? '#ff6b6b' : '#4285f4',
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#ffffff',
          },
        });

        // Agregar eventos de arrastre si es arrastrable
        if (isDraggable) {
          this.addDragEvents();
        }

        // No centrar automáticamente en create-post para mantener la vista del usuario
        if (this.currentMode !== 'create-post') {
          this.map.setCenter(this.selectedLocation);
        }
      } else {
        // Para móvil
        if (this.selectedLocationMarker) {
          await this.map.removeMarker(this.selectedLocationMarker);
        }

        const isDraggable = true;

        this.selectedLocationMarker = await this.map.addMarker({
          coordinate: this.selectedLocation,
          draggable: isDraggable,
        });

        if (isDraggable) {
          await this.addMobileDragEvents();
        }

        // No centrar automáticamente en create-post
        if (this.currentMode !== 'create-post') {
          await this.map.setCamera({
            coordinate: this.selectedLocation,
            zoom: 15,
            animate: true,
          });
        }
      }

      this.changeDetector.detectChanges();
    } catch (error) {
    }
  }

  // Método separado para agregar eventos de arrastre en web
  private addDragEvents() {
    if (!this.selectedLocationMarker) return;

    this.selectedLocationMarker.addListener('dragstart', () => {
      this.isLoadingAddress = true;
      this.selectedAddress = this.translate.instant('MAP.DRAGGING');
      this.changeDetector.detectChanges();
    });

    this.selectedLocationMarker.addListener('dragend', async (event: any) => {
      const newLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      this.selectedLocation = newLocation;
      this.addressDetails = {};
      this.selectedAddress = this.translate.instant('MAP.GETTING_NEW_ADDRESS');
      this.changeDetector.detectChanges();

      try {
        await this.getAddressFromCoordinates(newLocation);
      } catch (error) {
        this.selectedAddress = this.translate.instant('MAP.ADDRESS_ERROR');
        this.isLoadingAddress = false;
        this.changeDetector.detectChanges();
      }
    });
  }

  // Método separado para agregar eventos de arrastre en móvil
  private async addMobileDragEvents() {
    try {
      await this.map.setOnMarkerDragEndListener(async (event: any) => {
        if (event.markerId === this.selectedLocationMarker) {
          const newLocation = {
            lat: event.latitude,
            lng: event.longitude,
          };

          this.selectedLocation = newLocation;
          this.addressDetails = {};
          this.selectedAddress = this.translate.instant('MAP.GETTING_NEW_ADDRESS');
          this.isLoadingAddress = true;
          this.changeDetector.detectChanges();

          try {
            await this.getAddressFromCoordinates(newLocation);
          } catch (error) {
            this.selectedAddress = this.translate.instant('MAP.ADDRESS_ERROR');
            this.isLoadingAddress = false;
            this.changeDetector.detectChanges();
          }
        }
      });

      await this.map.setOnMarkerDragStartListener(async (event: any) => {
        if (event.markerId === this.selectedLocationMarker) {
          this.isLoadingAddress = true;
          this.selectedAddress = this.translate.instant('MAP.DRAGGING');
          this.changeDetector.detectChanges();
        }
      });
    } catch (error) {
    }
  }

  // Obtener dirección a partir de coordenadas usando GeocodingService
  async getAddressFromCoordinates(location: { lat: number; lng: number }) {
    if (!location) {
      return;
    }

    // Activar el indicador de carga
    this.isLoadingAddress = true;
    this.changeDetector.detectChanges();

    try {
      const result = await this.geocodingService.getAddressFromCoordinates(
        location,
        this.geocoder
      );

      if (result) {
        this.selectedAddress = result.address;
        this.addressDetails = {
          city: result.city,
          province: result.province,
          postalCode: result.postalCode,
          country: result.country,
        };
      } else {
        this.selectedAddress = this.translate.instant('MAP.ADDRESS_NOT_FOUND');
      }
    } catch (error) {
      this.selectedAddress = this.translate.instant('MAP.ADDRESS_ERROR');
    } finally {
      this.isLoadingAddress = false;
      this.changeDetector.detectChanges();
    }
  }

  // Confirmar la ubicación seleccionada con la información completa
  async setLocation() {
    if (!this.selectedLocation) {
      return;
    }

    this.isProcessing = true;
    this.changeDetector.detectChanges();

    try {
      // Preparar objeto de datos de ubicación
      const locationData = {
        lat: this.selectedLocation.lat,
        lng: this.selectedLocation.lng,
        address: this.selectedAddress,
        city: this.addressDetails.city,
        province: this.addressDetails.province,
        postalCode: this.addressDetails.postalCode,
        country: this.addressDetails.country,
        range: this.range,
      };

      // Guardar en localStorage como respaldo temporal
      this.storage.setJSON(StorageKeys.MAP_LOCATION_DATA, locationData);

      if (this.currentMode === 'filter') {
        // Para modo filter, navegar de regreso a listing
        this.router.navigate(['/listing'], {
          replaceUrl: true,
          state: {
            locationData: locationData,
            fromMapFilter: true,
          },
        });
      } else {
        // Limpiar el estado actual antes de navegar
        window.history.replaceState({}, '', window.location.href);

        // Manejar navegación según el modo
        if (this.currentMode === 'create-post') {
          // Navegar de regreso reemplazando la entrada actual
          this.router.navigate(['/create-post'], {
            replaceUrl: true,
            state: {
              locationData: locationData,
            },
          });
        } else if (this.currentMode === 'edit-post') {
          // Obtener el ID del post desde los queryParams
          const editPostId = this.route.snapshot.queryParams['returnTo']
            ?.split('/')
            .pop();

          // Navegar de regreso a edit-post reemplazando la entrada actual
          this.router.navigate(['/edit-post', editPostId], {
            replaceUrl: true,
            state: {
              locationData: locationData,
            },
          });
        } else {
          // Para otros modos, emitir el evento normalmente
          this.locationSelected.emit(locationData);
        }
      }
    } catch (error) {
    } finally {
      this.isProcessing = false;
      this.changeDetector.detectChanges();
    }
  }

  // Método para cancelar la selección y regresar
  cancelSelection() {
    if (this.currentMode === 'filter') {
      // Para cancelación en filtros, también regresar y reabrir modal
      this.router.navigate(['/listing'], {
        replaceUrl: true,
        state: {
          fromMapFilter: true,
        },
      });
    } else {
      // Limpiar el estado actual antes de navegar
      window.history.replaceState({}, '', window.location.href);

      // Navegar de regreso reemplazando la entrada actual
      this.router.navigate(['/create-post'], {
        replaceUrl: true,
      });
    }
  }

  // Actualizar el rango (si es necesario para tu aplicación)
  setRange(e: any) {
    this.range = e.detail.value;
  }

  ngOnDestroy() {
    try { document.body.classList.remove('map-screen-active'); } catch {}
  }
}
